import React, { useRef, useState } from "react";
import { InfoIcon, ArrowRightIcon } from "./icons";

const chartData = [
  { label: "May 14", leads: 28, rate: 10 },
  { label: "May 15", leads: 38, rate: 12 },
  { label: "May 16", leads: 42, rate: 13 },
  { label: "May 17", leads: 55, rate: 18 },
  { label: "May 18", leads: 40, rate: 14 },
  { label: "May 19", leads: 58, rate: 16 },
  { label: "May 20", leads: 52, rate: 15 },
];

const VB_WIDTH = 760;
const VB_HEIGHT = 300;
const PAD_LEFT = 34;
const PAD_RIGHT = 40;
const PAD_TOP = 20;
const PAD_BOTTOM = 28;
const PLOT_WIDTH = VB_WIDTH - PAD_LEFT - PAD_RIGHT;
const PLOT_HEIGHT = VB_HEIGHT - PAD_TOP - PAD_BOTTOM;
const Y_MAX_LEADS = 80;
const Y_MAX_RATE = 20;
const LEFT_TICKS = [0, 20, 40, 60, 80];
const RIGHT_TICKS = [0, 5, 10, 15, 20];

const bandWidth = PLOT_WIDTH / chartData.length;
const xCenter = (i) => PAD_LEFT + bandWidth * (i + 0.5);
const yAtLeads = (v) => PAD_TOP + PLOT_HEIGHT - (v / Y_MAX_LEADS) * PLOT_HEIGHT;
const yAtRate = (v) => PAD_TOP + PLOT_HEIGHT - (v / Y_MAX_RATE) * PLOT_HEIGHT;
const yBase = PAD_TOP + PLOT_HEIGHT;

const BAR_WIDTH = 22;

const linePath = chartData.map((d, i) => `${i === 0 ? "M" : "L"}${xCenter(i)},${yAtRate(d.rate)}`).join(" ");

export default function LeadsChart() {
  const [hovered, setHovered] = useState(null);
  const svgRef = useRef(null);

  const handleMove = (e) => {
    const rect = svgRef.current.getBoundingClientRect();
    const fraction = (e.clientX - rect.left) / rect.width;
    const vbx = fraction * VB_WIDTH;
    let index = Math.floor((vbx - PAD_LEFT) / bandWidth);
    index = Math.max(0, Math.min(chartData.length - 1, index));
    setHovered(index);
  };

  const active = hovered !== null ? chartData[hovered] : null;

  return (
    <section className="dash-card">
      <div className="dash-card-header">
        <h2 className="dash-card-title">
          Leads over time
          <span className="an-info-icon" title="Daily leads captured and the share of conversations that converted.">
            <InfoIcon />
          </span>
        </h2>
      </div>

      <div className="an-legend">
        <span className="an-legend-item">
          <span className="an-legend-swatch" />
          Leads
        </span>
        <span className="an-legend-item">
          <span className="an-legend-line an-legend-line--rate" />
          Conversion rate (%)
        </span>
      </div>

      <div className="dash-chart-wrap">
        <svg
          ref={svgRef}
          className="dash-chart-svg"
          viewBox={`0 0 ${VB_WIDTH} ${VB_HEIGHT}`}
          preserveAspectRatio="none"
          onMouseMove={handleMove}
          onMouseLeave={() => setHovered(null)}
          role="img"
          aria-label="Leads captured and conversion rate over time"
        >
          {LEFT_TICKS.map((tick) => (
            <line
              key={tick}
              x1={PAD_LEFT}
              x2={VB_WIDTH - PAD_RIGHT}
              y1={yAtLeads(tick)}
              y2={yAtLeads(tick)}
              className="dash-chart-gridline"
            />
          ))}

          {LEFT_TICKS.map((tick) => (
            <text key={tick} x={PAD_LEFT - 10} y={yAtLeads(tick) + 4} className="dash-chart-ytick" textAnchor="end">
              {tick}
            </text>
          ))}

          {RIGHT_TICKS.map((tick) => (
            <text
              key={tick}
              x={VB_WIDTH - PAD_RIGHT + 10}
              y={yAtRate(tick) + 4}
              className="dash-chart-ytick"
              textAnchor="start"
            >
              {tick}%
            </text>
          ))}

          {chartData.map((d, i) => {
            const height = yBase - yAtLeads(d.leads);
            return (
              <rect
                key={d.label}
                x={xCenter(i) - BAR_WIDTH / 2}
                y={yAtLeads(d.leads)}
                width={BAR_WIDTH}
                height={height}
                rx="4"
                className={`an-leads-bar${hovered === i ? " is-active" : ""}`}
              />
            );
          })}

          {hovered !== null && (
            <line x1={xCenter(hovered)} x2={xCenter(hovered)} y1={PAD_TOP} y2={yBase} className="dash-chart-crosshair" />
          )}

          <path d={linePath} className="an-rate-line" />

          {chartData.map((d, i) => (
            <circle
              key={d.label}
              cx={xCenter(i)}
              cy={yAtRate(d.rate)}
              r={hovered === i ? 6 : 4}
              className="an-rate-dot"
            />
          ))}

          {chartData.map((d, i) => {
            const isFirst = i === 0;
            const isLast = i === chartData.length - 1;
            return (
              <text
                key={`x-${d.label}`}
                x={xCenter(i)}
                y={VB_HEIGHT - 6}
                className="dash-chart-xtick"
                textAnchor={isFirst ? "start" : isLast ? "end" : "middle"}
              >
                {d.label}
              </text>
            );
          })}
        </svg>

        {active && (
          <div
            className="dash-chart-tooltip an-chart-tooltip"
            style={{
              left: `${(xCenter(hovered) / VB_WIDTH) * 100}%`,
              top: `${(yAtRate(active.rate) / VB_HEIGHT) * 100}%`,
            }}
          >
            <span className="dash-chart-tooltip-date">{active.label}, 2024</span>
            <span className="dash-chart-tooltip-value">
              <span className="dash-chart-tooltip-dot" />
              {active.leads} leads
            </span>
            <span className="dash-chart-tooltip-value an-chart-tooltip-secondary">
              <span className="dash-chart-tooltip-dot an-chart-tooltip-dot-rate" />
              {active.rate}% conversion
            </span>
          </div>
        )}
      </div>

      <a href="#full-report" className="dash-card-link an-view-link">
        View full report
        <ArrowRightIcon />
      </a>
    </section>
  );
}
